import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MakersFinancesMgrService } from '../../services/makers-finances-mgr.service';
import { StoreAuxService } from '../../../services/storeAux/store-aux.service';
import { AlertsService } from '../../../services/alerts/alerts.service';
import { AlertsType } from '@rappi/ui/alerts';
import { CompanyRecord } from '../../models/makers-finances-mgr.models';
import {
  COMPANY_STATUS_LABELS,
  COMPANY_TYPE_LABELS,
  CompanyStatus,
  CompanyStatusMeta,
  CompanyType,
} from '../companies/companies.constants';

// A detail field: a label + the value to display. Empty values fall back to PLACEHOLDER so the
// 2-column grid layout is preserved even when the backend hasn't returned a field yet.
interface DetailField {
  label: string;
  value: string;
}

// A detail section (card): a title + its fields laid out in a 2-column grid.
interface DetailSection {
  title: string;
  fields: DetailField[];
}

// Placeholder shown for fields the current endpoint response does not include (Figma shows ~40
// fields; CompanyRecord has 27). Keeps the layout intact until the backend exposes them.
const PLACEHOLDER = '-';

@Component({
  selector: 'app-company-detail',
  templateUrl: './company-detail.component.html',
  styleUrls: ['./company-detail.component.scss'],
})
export class CompanyDetailComponent implements OnInit {
  loading = true;
  company: CompanyRecord | null = null;
  sections: DetailSection[] = [];

  // Expose constants to the template (templates cannot import constants directly).
  readonly STATUS_LABELS = COMPANY_STATUS_LABELS;
  readonly TYPE_LABELS = COMPANY_TYPE_LABELS;
  readonly PLACEHOLDER = PLACEHOLDER;

  // Resolves the status display metadata for the header badge. Falls back to the raw code when the
  // status isn't mapped so the badge renders without crashing.
  statusMeta(status: string): CompanyStatusMeta {
    return this.STATUS_LABELS[status as CompanyStatus] ?? { label: status, cssClass: status };
  }

  // Skeleton placeholder data — each entry represents one card; fields.length controls the
  // number of shimmer bars shown in the 2-column grid while the real data loads.
  readonly skeletonCards = [
    { fields: Array(6) },  // Company
    { fields: Array(8) },  // Service level
    { fields: Array(11) }, // Compliance & Address
    { fields: Array(15) }, // Invoicing instructions
    { fields: Array(9) },  // Audit
  ];
  readonly skeletonSidebarCards = [
    { fields: Array(2) },  // Approval
  ];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly financesService: MakersFinancesMgrService,
    private readonly storeAux: StoreAuxService,
    private readonly alertsService: AlertsService,
  ) {}

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const id = Number(idParam);
    if (!idParam || !Number.isFinite(id)) {
      this.router.navigate(['/financial-hub/companies']);
      return;
    }

    this.financesService.getCompany(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.company = res.data;
          this.sections = this.buildSections(res.data);
          this.loading = false;
        },
        error: () => {
          this.alertsService.openAlerts(
            'Could not load the company. Please try again.',
            AlertsType.error,
          );
          this.router.navigate(['/financial-hub/companies']);
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // TODO: wired once the approve/return endpoints are available. Currently a no-op stub so the
  // Approval panel (Figma node 1596:69048) renders correctly without backend support.
  approveCompany(): void {}

  // TODO: see approveCompany.
  returnCompany(): void {}

  private yesNo(value: boolean | null | undefined): string {
    return value ? 'Yes' : 'No';
  }

  // Formats an ISO timestamp as a short, readable date. Returns the placeholder for empty values.
  private formatDate(iso: string | null | undefined): string {
    if (!iso) {
      return PLACEHOLDER;
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return PLACEHOLDER;
    }
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  // Builds the 5 detail sections from the Figma design (node 1596:68903). Fields not yet present
  // in CompanyRecord render as PLACEHOLDER so the layout matches the design; they'll be mapped as
  // the backend exposes them.
  private buildSections(c: CompanyRecord): DetailSection[] {
    const f = (label: string, value: string | null | undefined): DetailField =>
      ({ label, value: value || PLACEHOLDER });

    return [
      {
        title: 'Company',
        fields: [
          f('Country', c.country),
          f('Company ID', c.maker_code),
          f('Commercial Name', c.name),
          f('Company Type', this.TYPE_LABELS[c.type_of_company as CompanyType]),
          f('Company Group', PLACEHOLDER),
          f('International company', this.yesNo(c.is_international_company)),
        ],
      },
      {
        title: 'Service level',
        fields: [
          f('Account manager', c.account_manager_email),
          f('2nd account manager', PLACEHOLDER),
          f('Center of Excellence (COE)', PLACEHOLDER),
          f('International agency', PLACEHOLDER),
          f('Tier of company', PLACEHOLDER),
          f('Conciliation Manager', c.conciliation_manager_email),
          f('Collector', PLACEHOLDER),
          f('Squad', PLACEHOLDER),
        ],
      },
      {
        title: 'Compliance & Address',
        fields: [
          f('Tax ID', c.tax_id),
          f('Legal Name', c.social_reason),
          f('Type of Tax ID', c.type_of_tax_id),
          f('Tax regime', PLACEHOLDER),
          f('Contributor class', PLACEHOLDER),
          f('Individual', PLACEHOLDER),
          f('Check digit', PLACEHOLDER),
          f('Legal POC', PLACEHOLDER),
          f('Address', PLACEHOLDER),
          f('City ID', PLACEHOLDER),
          f('Postal code', PLACEHOLDER),
        ],
      },
      {
        title: 'Invoicing instructions',
        fields: [
          f('Payment terms', c.payment_terms),
          f('Billing criteria', PLACEHOLDER),
          f('Billing frequency', PLACEHOLDER),
          f('Financial cut-off day', c.financial_cutoff_day != null ? String(c.financial_cutoff_day) : PLACEHOLDER),
          f('Financial POC', PLACEHOLDER),
          f('Invoicing email', c.invoicing_email),
          f('Inquires email', PLACEHOLDER),
          f('Phone number', PLACEHOLDER),
          f('Requires invoice upload portal', this.yesNo(c.requires_invoice_upload)),
          f('Invoice portal URL', PLACEHOLDER),
          f('Proof of execution requirements', c.proof_of_execution_requirements),
          f('Requires good receipt', this.yesNo(c.requires_good_receipt)),
          f('OC Required', PLACEHOLDER),
          f('Signed by both parties', PLACEHOLDER),
          f('Invoice comments', PLACEHOLDER),
        ],
      },
      {
        title: 'Audit',
        fields: [
          f('Settlement Participant ID', PLACEHOLDER),
          f('Settlement Contract ID', PLACEHOLDER),
          f('Settlement Payment Reference ID', PLACEHOLDER),
          f('Legal document ID', PLACEHOLDER),
          f('Created by/at', this.formatDate(c.created_at)),
          f('Last modified by/at', this.formatDate(c.updated_at)),
          f('Last approved by/at', PLACEHOLDER),
          f('Approved at', this.formatDate(c.verified_at)),
          f('Closed by/at', PLACEHOLDER),
        ],
      },
    ];
  }
}
